// | Money type.
// |
// | Some terminology used here:
// | - _currency unit_ - one basic unit of the currency used, e.g. 1 USD, or 1 PLN.
// | - _cent_ - 1/100 of the currency unit, e.g. 1 grosz in case of PLN.
// |
// | When a result needs to rounded, _banker's rounding_ is used (also called _half to even rounding_).
// | It has the property `round(-x) = -round(x)`. Therefore we can calculate e.g. discounts either as negative or positive values,
// | and get the same result either way.
// |
// | Functions which round results are marked with `AndRound` suffix.
import * as $runtime from "../runtime.js";
import * as Data$dEither from "../Data.Either/index.js";
import * as Data$dEq from "../Data.Eq/index.js";
import * as Data$dIdentity from "../Data.Identity/index.js";
import * as Data$dInt from "../Data.Int/index.js";
import * as Data$dMaybe from "../Data.Maybe/index.js";
import * as Data$dNumber from "../Data.Number/index.js";
import * as Data$dNumber$dFormat from "../Data.Number.Format/index.js";
import * as Data$dOrd from "../Data.Ord/index.js";
import * as Data$dShow from "../Data.Show/index.js";
import * as Data$dString$dCommon from "../Data.String.Common/index.js";
import * as Data$dString$dRegex from "../Data.String.Regex/index.js";
import * as Data$dString$dRegex$dFlags from "../Data.String.Regex.Flags/index.js";
import * as Foreign from "../Foreign/index.js";
import * as $$Math from "../Math/index.js";
import * as Partial from "../Partial/index.js";
import * as Unscramble from "../Unscramble/index.js";
const Money = x => x;
const toNumber = v => Data$dInt.toNumber(v) / 100.0;
const toString = /* #__PURE__ */ (() => {
  const $0 = Data$dNumber$dFormat.toStringWith(Data$dNumber$dFormat.fixed(2));
  return x => $0(Data$dInt.toNumber(x) / 100.0);
})();
const toCents = v => v;
const showMoney = {show: toString};
const semigroupMoney = {append: v => v1 => v + v1 | 0};
const scaleIntMoney = {scale: i => v => i * v | 0};
const roundCents = n => {
  const abs = $$Math.abs(n);
  const fraction = abs - $$Math.floor(abs);
  const integer = Data$dInt.unsafeClamp(Data$dNumber.floor(abs));
  return Data$dInt.unsafeClamp(Data$dNumber.floor((() => {
    if (n < 0.0) { return -1.0; }
    if (n > 0.0) { return 1.0; }
    return n;
  })())) * (() => {
    if (fraction === 0.5 && (integer & 1) === 0) { return integer; }
    if (fraction === 0.5 && (integer & 1) !== 0) { return integer + 1 | 0; }
    return Data$dInt.unsafeClamp(Data$dNumber.round(abs));
  })() | 0;
};
const scaleByFractionAndRound = numerator => denominator => v => roundCents(Data$dInt.toNumber(v * numerator | 0) / Data$dInt.toNumber(denominator));
const ordMoney = Data$dOrd.ordInt;
const monoidMoney = {mempty: 0, Semigroup0: () => semigroupMoney};
const moneyRE = /* #__PURE__ */ (() => {
  const $0 = Data$dString$dRegex.regex("^-?[0-9]+([,\\.][0-9]{0,2})?$")(Data$dString$dRegex$dFlags.noFlags);
  if ($0.tag === "Left") { return Partial._crashWith(Data$dShow.showStringImpl($0._1)); }
  if ($0.tag === "Right") { return $0._1; }
  $runtime.fail();
})();
const udecodeMoney = {unsafeDecode: x => Data$dInt.unsafeClamp(Data$dNumber.round(Unscramble.decodeNumber(x) * 100.0))};
const fromNumber = n => roundCents(n * 100.0);
const fromString = v => {
  if (Data$dString$dRegex.test(moneyRE)(v)) {
    const $0 = Data$dNumber.fromStringImpl(Data$dString$dCommon.replace(",")(".")(v), Data$dNumber.isFinite, Data$dMaybe.Just, Data$dMaybe.Nothing);
    if ($0.tag === "Just") { return Data$dMaybe.$Maybe("Just", roundCents($0._1 * 100.0)); }
    return Data$dMaybe.Nothing;
  }
  return Data$dMaybe.Nothing;
};
const scaleAndRound = n => m => roundCents(n * (Data$dInt.toNumber(m) / 100.0) * 100.0);
const scaleByPercentAndRound = p => scaleAndRound(p * 0.01);
const fromCents = Money;
const eqMoney = Data$dEq.eqInt;
const encodeMoney = {encode: x => Data$dInt.toNumber(x) / 100.0};
const decodeMoney = {
  decode: f => {
    const $0 = Foreign.unsafeReadTagged(Data$dIdentity.monadIdentity)("Number")(f);
    if ($0.tag === "Left") { return Data$dEither.$Either("Left", $0._1); }
    if ($0.tag === "Right") { return Data$dEither.$Either("Right", Data$dInt.unsafeClamp(Data$dNumber.round($0._1 * 100.0))); }
    $runtime.fail();
  }
};
const additiveMoney = {zero: 0, add: v => v1 => v + v1 | 0, sub: v => v1 => v - v1 | 0};
const avgAndRound = dictFoldable => f => roundCents(Data$dInt.toNumber(dictFoldable.foldl(additiveMoney.add)(0)(f) * 1 | 0) / Data$dInt.toNumber(dictFoldable.foldl(c => v => 1 + c | 0)(0)(f)));
export {
  Money,
  additiveMoney,
  avgAndRound,
  decodeMoney,
  encodeMoney,
  eqMoney,
  fromCents,
  fromNumber,
  fromString,
  moneyRE,
  monoidMoney,
  ordMoney,
  roundCents,
  scaleAndRound,
  scaleByFractionAndRound,
  scaleByPercentAndRound,
  scaleIntMoney,
  semigroupMoney,
  showMoney,
  toCents,
  toNumber,
  toString,
  udecodeMoney
};
